import React from "react";

import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { HttpErrorDto } from "@common/dto/http";
import { trDts } from "@modules/datasheets/utils/i18n";
import isNil from "lodash/isNil";
import { useMessage } from "@common/hooks/message";

export type DatasheetFormatManagementFormat = {
  id: number;
  name: string;
};

export type DatasheetSaveAsProps = {
  onSubmit: (payload: { format: DatasheetFormatManagementFormat; folder: FolderPrimaryListDto }) => Promise<void>;
  onClose: () => void;
  getFormats: (folderId: FolderPrimaryListDto["id"]) => Promise<Array<DatasheetFormatManagementFormat>>;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  deleteFormat: (
    folderId: FolderPrimaryListDto["id"],
    formatId: DatasheetFormatManagementFormat["id"]
  ) => Promise<void>;
  deleteFolder: (id: FolderPrimaryListDto["id"]) => Promise<void>;
  createFolder: (params: { foldername: string; parentid?: number }) => Promise<void>;
};

export const useDatasheetSaveAsController = (props: DatasheetSaveAsProps) => {
  const { onSubmit, getFormats, getFolders, deleteFormat, deleteFolder, createFolder } = props;
  const { messageProps, showErrorMessage, closeMessage } = useMessage();

  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [formats, setFormats] = React.useState<Array<DatasheetFormatManagementFormat>>([]);
  const [isSaveAsLoading, setIsSaveAsLoading] = React.useState(false);

  const getAndSetFormats = React.useCallback(
    async (folderId: number) => {
      const res = await getFormats(folderId);
      setFormats(res);
    },
    [getFormats]
  );

  const getAndSetFolders = React.useCallback(async () => {
    const res = await getFolders();
    setFolders(res);
  }, [getFolders]);

  const handleFormatDelete = React.useCallback(
    async (folderId: number, formatId: number) => {
      await deleteFormat(folderId, formatId);
      await getAndSetFormats(folderId);
    },
    [deleteFormat, getAndSetFolders]
  );

  const handleFolderDelete = React.useCallback(
    async (folderId: number) => {
      await deleteFolder(folderId);
      await getAndSetFolders();
    },
    [deleteFolder, getAndSetFolders]
  );

  const handleFolderCreate = React.useCallback(
    async (data: { parentId?: number; name: string }) => {
      await createFolder({
        parentid: data.parentId,
        foldername: data.name,
      });
      await getAndSetFolders();
    },
    [createFolder, getAndSetFolders]
  );

  const handleSaveAsSubmit = React.useCallback(
    async (format: DatasheetFormatManagementFormat, folder: FolderPrimaryListDto) => {
      try {
        setIsSaveAsLoading(true);

        await onSubmit({
          folder,
          format,
        });
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to save datasheet format"));
      } finally {
        setIsSaveAsLoading(false);
      }
    },
    [onSubmit, showErrorMessage]
  );

  const isAllowedSaveAsSubmit = React.useCallback(
    ({ newFormatName, folderId }: { newFormatName?: string; folderId?: number }) =>
      Boolean(newFormatName?.trim().length) && folderId?.toString() !== "" && !isNil(folderId) && !isSaveAsLoading,
    [isSaveAsLoading]
  );

  return {
    folders,
    formats,
    isSaveAsLoading,
    handleFormatDelete,
    handleFolderDelete,
    handleFolderCreate,
    getAndSetFormats,
    getAndSetFolders,
    handleSaveAsSubmit,
    isAllowedSaveAsSubmit,
    messageProps,
    closeMessage,
  };
};
